import { useEffect, useRef, useState } from "react";
import { IDS } from "../constants/testIds";

/**
 * A small live seismogram-style ticker. Reads the wind level from a ref so
 * it moves in lockstep with the fur. We push a sample every 50ms and render
 * as an SVG polyline (last N samples).
 */
export default function Seismogram({ windLevelRef, awakeRef, width = 320, height = 60 }) {
  const SAMPLES = 200;
  const [, force] = useState(0);
  const samples = useRef(new Array(SAMPLES).fill(0));
  const raf = useRef(null);
  const lastSample = useRef(0);

  useEffect(() => {
    const loop = (t) => {
      if (t - lastSample.current > 45) {
        lastSample.current = t;
        // Base wind + a bit of noise mimicking ground micro-tremor
        const w = windLevelRef.current ?? 0.05;
        const noise = (Math.random() - 0.5) * (0.5 + w * 2.4);
        const carrier = Math.sin(t * 0.008) * (0.15 + w * 0.4);
        const ripple = Math.sin(t * 0.032) * (0.1 + w * 0.3);
        const spike = awakeRef.current ? (Math.random() - 0.5) * 1.8 : 0;
        const v = Math.max(-1, Math.min(1, carrier + ripple + noise * 0.55 + spike));
        samples.current.shift();
        samples.current.push(v);
        force((n) => (n + 1) % 1000);
      }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [windLevelRef, awakeRef]);

  // Recompute each render (cheap: 200 points, driven by the RAF force update)
  const step = width / (SAMPLES - 1);
  const mid = height / 2;
  const points = samples.current
    .map((v, i) => `${(i * step).toFixed(1)},${(mid - v * mid * 0.9).toFixed(1)}`)
    .join(" ");

  return (
    <div className="border border-[hsl(var(--ink))]/40 bg-[hsl(var(--paper-2))]/60 px-3 py-2" data-testid={IDS.seismogram}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--ink-2))]">Ground motion</span>
        <span className="mono text-[10px] text-[hsl(var(--ink-mid))]">live</span>
      </div>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke="hsl(24 22% 10%)" strokeWidth="0.4" opacity="0.35" strokeDasharray="2 3" />
        <polyline
          fill="none"
          stroke="hsl(14 66% 32%)"
          strokeWidth="1.2"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={points}
        />
      </svg>
    </div>
  );
}
